import { createContext, useCallback, useContext, useMemo, useRef } from 'react'
import type { ReactNode } from 'react'
import {
  getFullscriptClient,
  type FullscriptClient,
  type FullscriptFeature,
  type FullscriptPlatformOptions,
} from './sdk'

interface MountArgs extends FullscriptPlatformOptions {
  /** DOM element id to mount into. Fullscript's SDK takes a string id,
   *  not a ref — caller must guarantee the element exists at mount time
   *  (LabOrderEmbed waits via rAF + DOM-poll). */
  elementId: string
}

interface FullscriptEmbedContextValue {
  /** Mount the `'platform'` component into the given element id. Returns
   *  the feature handle so the caller can `.unmount()` later. */
  mountPlatform: (args: MountArgs) => Promise<FullscriptFeature>
  /** Imperative escape hatch — most consumers should use `mountPlatform`. */
  getClient: () => FullscriptClient
}

const FullscriptEmbedContext = createContext<FullscriptEmbedContextValue | null>(null)

interface FullscriptEmbedProviderProps {
  children: ReactNode
}

/**
 * App-wide provider for Fullscript Embed.
 *
 * Lifecycle:
 *   - Lazy: client is built on first `mountPlatform` call (the SDK package
 *     is bundled but the publicKey/env init only runs when needed).
 *   - Singleton: one `FullscriptClient` shared across the whole app —
 *     Fullscript's SDK design assumes you initialise the factory once.
 *
 * Wrap the clinician app at the layout level (above any route that hosts
 * a lab-orders embed) so init is shared across mount points.
 */
export default function FullscriptEmbedProvider({ children }: FullscriptEmbedProviderProps) {
  const clientRef = useRef<FullscriptClient | null>(null)

  const getClient = useCallback(() => {
    if (!clientRef.current) clientRef.current = getFullscriptClient()
    return clientRef.current
  }, [])

  const mountPlatform = useCallback(
    async ({ elementId, ...options }: MountArgs) => {
      const client = getClient()
      const feature = client.create('platform', options)
      await feature.mount(elementId)
      return feature
    },
    [getClient],
  )

  const value = useMemo<FullscriptEmbedContextValue>(
    () => ({ mountPlatform, getClient }),
    [mountPlatform, getClient],
  )

  return <FullscriptEmbedContext.Provider value={value}>{children}</FullscriptEmbedContext.Provider>
}

/** Hook used by embed components. Throws if used outside the provider so
 *  misuse fails loudly in dev. */
export const useFullscriptEmbed = (): FullscriptEmbedContextValue => {
  const ctx = useContext(FullscriptEmbedContext)
  if (!ctx) {
    throw new Error(
      'useFullscriptEmbed must be used inside <FullscriptEmbedProvider>. Wrap the clinician app root.',
    )
  }
  return ctx
}
